import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import { type CustomerProductRating, StatusCode } from '@hungryroot/rest';
import {
  patchApiV2CustomersCustomerIdProductRatingsId,
  type PatchedCustomerProductRating,
} from '@hungryroot/rest/api/v2/customers/[id]/product_ratings/[id]/patch';

type UsePatchCustomerProductRatingsProps<Context> = Omit<
  UseMutationOptions<
    CustomerProductRating,
    Error,
    { customerId: number; payload: PatchedCustomerProductRating },
    Context
  >,
  'mutationFn'
>;

export function usePatchCustomerProductRatings<Context>(
  props?: UsePatchCustomerProductRatingsProps<Context>
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      customerId: number;
      payload: PatchedCustomerProductRating;
    }) => {
      const { customerId, payload } = params;
      const res = await patchApiV2CustomersCustomerIdProductRatingsId({
        customerId,
        id: payload.id,
        requestBody: payload,
      });
      if (res.status !== StatusCode.SuccessOK) {
        throw new Error(JSON.stringify(res.data, null, 2));
      }
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['Customers', 'customers/:id/product-ratings/:ratingId'],
      });
    },
    ...props,
  });
}
